import React from 'react';
import CreateJobStep from './CreateJobStep';
import './CreateJobLogFilesStep.scss';
import parseCallLogFile from '../../helpers/callLogFiles/parseCallLogFile';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store/actions/appDispatch';
import {
	callLogFileImported, chooseCallLogXlsFileSheet,
	nextCreateJobStep,
	removeCallLogFile
} from '../../store/actions/createJobActions';
import documentIcon from '../../imgs/icons/document.svg';
import { ChooseLogFilesStep, ParsedCallLogXlsFile } from '../../store/state/createJobState';
import CreateJobPrevStep from './CreateJobPrevStep';
import { AppState } from '../../store/state/appState';
import { getCallLogDataValidationError, isCurrentCreateJobStepValid } from '../../store/selectors/createJobSelectors';
import FileUploadBox from '../FileUploadBox';
import { CallLogParseErrorResult } from '../../models/callLogFileHandler';

export default function CreateJobLogFilesStep(props: Props) {
	const isStepValid = useSelector<AppState, boolean>(s => isCurrentCreateJobStepValid(s, props.engagementId));
	const dispatch = useDispatch<AppDispatch>();
	const validationError = getCallLogDataValidationError(props.step);
	
	return (
		<React.Fragment>
			<CreateJobStep progressStatus="select" engagementId={props.engagementId}>
				<div className="page-container text-center">
					<h1 className="h1-mb-1">Select call logs</h1>

					<div className="mb-7">
						Creovai needs to know about each call you plan to upload. Provide a file that contains file names, start times, call IDs and more. 
						<span className="text-bold">
						 	&nbsp;If uploading multiple files, please ensure each document contains the same headers or data schema.
						</span>
					</div>

					<div className="flex flex-align-center text-left mb-6">
						<div className="flex-item-growing-shrinking">
							<h3>Add files to upload</h3>
						</div>
						<div className="flex-item-static">
							<div className="mr-2"><CreateJobPrevStep engagementId={props.engagementId} /></div>
						</div>
						<div className="flex-item-static">
							<button className="btn btn_wide-2" disabled={!isStepValid} onClick={() => dispatch(nextCreateJobStep(props.engagementId))}>Continue</button>
						</div>
					</div>

					<div className="mb-4">
						<FileUploadBox onFilesSelected={importFiles}
									   header="Drag and drop your file here or browse:"
									   subHeader="Creovai currently only accepts .csv, .xls, and .xlsx files"
									   accept=".csv, .xls, .xlsx" />
					</div>		

					{!!validationError &&
						<div className="mb-4 text-center text-bold text-red">{validationError}</div>
					}
					
					<div className="text-left">
						{!!props.step.files.length &&
							<React.Fragment>
								<div className="boxed-list__container">
									{props.step.files.map((f, i) => (
										<div className="boxed-list__item" key={i}>
											<div className="flex flex-align-center">
												<img className="flex-item-static" src={documentIcon}  style={{height: 18 }} alt="Document" />
												<div className="flex-item-growing-shrinking create-job-files-step__selected-filename">{f.filename ?? 'Unknown file'}</div>
												{f.type === 'xls' &&
													<div className="flex-item-static">Select sheet&nbsp;
														<select className="create-job-files-step__sheet-selector" placeholder="Select a sheet..." value={f.selectedSheet.sheetId} onChange={e => handleUpdateSelectedSheet(f, e.target.value)}>
															{f.allSheets.map((sheet, i) => (
																<option value={sheet.sheetId} key={i}>{sheet.name}</option>
															))}
														</select>
													</div>
												}
												<div className="flex-item-static">
													<span className="link" onClick={() => dispatch(removeCallLogFile(props.engagementId, f.id))}>Remove</span>
												</div>
											</div>
										</div>
									))}
								</div>
							</React.Fragment>
						}
					</div>
				</div>
			</CreateJobStep>
		</React.Fragment>
	);
	
	function importFiles(files: File[]) {
		files.forEach(file => {
			parseCallLogFile(file).then(result => {
				if (result.type === 'success') {
					dispatch(callLogFileImported(props.engagementId, result.file));
				} else {
					toast.error(`Unable to upload ${file.name ?? 'file'}. ${getFileParseErrorMessage(result)}`);
				}
			}, reason => {
				// we shouldn't really hit this since errors
				// are supposed to be handled and returned in the
				// resolve cb.
				toast.error(`Unable to upload ${file.name ?? 'file'}`);
			});
		});
	}
	
	function handleUpdateSelectedSheet(file: ParsedCallLogXlsFile, sheetId: string): void {
		dispatch(chooseCallLogXlsFileSheet(props.engagementId, file.id, sheetId));
	}
}

function getFileParseErrorMessage(result: CallLogParseErrorResult): string {
	switch (result.error.type) {
		case 'empty':
			return 'File was empty.';
		case 'parseFailed':
			return 'Failed to parse the file.';
		case 'unsupportedFileType':
			return 'File type is not supported.';
	}
}

interface Props {
	step: ChooseLogFilesStep;
	engagementId: number;
}
